import { LIMITE_TEXTO, type Parceiro, type Recado, type SituacaoRecado } from './MensagemDireta.scopes'

export type Cor = 'success' | 'warning' | 'error' | 'info' | 'default'

export const SITUACOES: Record<SituacaoRecado, { rotulo: string; cor: Cor; ajuda: string }> = {
    pendente: {
        rotulo: 'Pendente',
        cor: 'warning',
        ajuda: 'Aguardando o parceiro: o recado sai quando ele tocar em "Ver mensagem" ou escrever à LIA.'
    },
    enviado: { rotulo: 'Enviado', cor: 'info', ajuda: 'Saiu pela LIA; a Meta ainda não confirmou a entrega.' },
    entregue: { rotulo: 'Entregue', cor: 'success', ajuda: 'Chegou ao WhatsApp do parceiro.' },
    lido: { rotulo: 'Lido', cor: 'success', ajuda: 'O parceiro leu.' },
    falhou: { rotulo: 'Falhou', cor: 'error', ajuda: 'A Meta não entregou.' },
    cancelado: { rotulo: 'Cancelado', cor: 'default', ajuda: 'Cancelado antes de sair.' }
}

const AVISO: Record<NonNullable<Recado['avisoSituacao']>, string> = {
    enviado: 'enviado',
    entregue: 'entregue',
    lido: 'lido',
    falhou: 'não entregue'
}

/** O detalhe da situação, para a dica do chip. */
export function detalheDaSituacao(r: Recado): string {
    const base = SITUACOES[r.situacao]?.ajuda ?? ''
    const partes = [base]
    if (r.situacao === 'falhou' && r.erro) partes.push(`Erro: ${r.erro}.`)
    if (r.situacao === 'cancelado' && r.canceladoPor) partes.push(`Por ${r.canceladoPor} em ${quando(r.canceladoEm)}.`)
    if (r.avisoEm && r.avisoSituacao) {
        partes.push(`Aviso ${AVISO[r.avisoSituacao]} em ${quando(r.avisoEm)}${r.avisoErro ? ` (${r.avisoErro})` : ''}.`)
    }
    return partes.join(' ')
}

/** Como o recado vai sair para este parceiro, para o operador saber antes de enviar. */
export function comoVaiSair(p: Parceiro): { severity: 'success' | 'info' | 'error'; texto: string } {
    if (!p.telefone) return { severity: 'error', texto: 'O parceiro não tem telefone principal válido.' }
    if (p.bloqueado) {
        return { severity: 'error', texto: 'O número do parceiro está bloqueado: a LIA não fala com ele.' }
    }
    if (p.janelaAberta) {
        return {
            severity: 'success',
            texto: `O parceiro escreveu à LIA nas últimas 24 horas (${quando(p.ultimaMensagem)}): o recado vai na hora.`
        }
    }
    const pendentes =
        p.pendentes > 0
            ? ` Ele já tem ${p.pendentes} recado${p.pendentes > 1 ? 's' : ''} pendente${
                  p.pendentes > 1 ? 's' : ''
              }: não vai outro aviso.`
            : ''
    return {
        severity: 'info',
        texto:
            `O parceiro não escreve à LIA há mais de 24 horas${
                p.ultimaMensagem ? ` (última vez em ${quando(p.ultimaMensagem)})` : ''
            }. O recado fica pendente e a LIA manda um aviso com o botão "Ver mensagem"; ele sai quando o parceiro ` +
            `tocar no botão ou escrever.${pendentes}`
    }
}

export function podeEnviar(p: Parceiro | undefined, texto: string): boolean {
    return !!p && !!p.telefone && !p.bloqueado && texto.trim().length > 0 && texto.length <= LIMITE_TEXTO
}

export function contador(texto: string): string {
    return `${texto.length.toLocaleString('pt-BR')} / ${LIMITE_TEXTO.toLocaleString('pt-BR')}`
}

/** 5548999998888 → +55 (48) 99999-8888 */
export function formatarTelefone(telefone?: string): string {
    if (!telefone) return '—'
    const d = telefone.replace(/\D/g, '')
    const m = d.match(/^55(\d{2})(\d{4,5})(\d{4})$/)
    return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : `+${d}`
}

export function quando(iso?: string): string {
    return iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'
}
